import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Briefcase } from 'lucide-react';

const experiences = [
  {
    company: 'Amazon',
    role: 'IT Support Engineer I',
    location: 'Los Angeles, CA',
    period: 'Sep 2020 – Present',
    isCurrent: true,
    responsibilities: [
      'Support enterprise IT infrastructure across large-scale Amazon fulfillment and robotics facilities',
      'Serve as IT Deployment Engineer on facility builds, expansions, and retrofit projects',
      'Deploy and support network equipment including switches, routers, firewalls, wireless access points, and structured cabling',
      'Support core network services such as DNS, DHCP, VLANs, and IP addressing in segmented environments',
      'Install and support Windows and Linux servers, end-user systems, and peripheral hardware',
      'Collaborate with vendors, engineering teams, and internal stakeholders on infrastructure initiatives',
      'Participate in on-call rotations and incident response for production systems',
      'Create SOPs and internal documentation; mentor and support junior technicians'
    ]
  },
  {
    company: 'Amazon',
    role: 'IT Support Engineer I',
    location: 'Dallas–Fort Worth, TX',
    period: 'Nov 2019 – Sep 2020',
    isCurrent: false,
    responsibilities: [
      'Supported enterprise IT infrastructure and network operations within large-scale fulfillment facilities',
      'Performed hands-on network deployment, structured cabling, and hardware installation',
      'Assisted with network troubleshooting and day-to-day operational support',
      'Supported switches, routers, wireless access points, and end-user systems'
    ]
  },
  {
    company: 'Bicycle Transit Systems',
    role: 'Systems Administrator',
    location: 'Los Angeles, CA',
    period: 'Jul 2017 – Nov 2019',
    isCurrent: false,
    responsibilities: [
      'Administered Azure Active Directory, identity and access management, and Group Policy',
      'Supported Line-of-Business applications and SharePoint sites, permissions, and collaboration tools',
      'Monitored systems and networks to ensure availability, performance, and security',
      'Used ticketing systems to manage support requests and documentation',
      'Researched and recommended system improvements and automation opportunities'
    ]
  },
  {
    company: 'California Medical Evaluators',
    role: 'IT Support Administrator',
    location: 'Los Angeles, CA',
    period: 'Dec 2015 – Jul 2017',
    isCurrent: false,
    responsibilities: [
      'Installed, configured, and supported computing systems in a healthcare environment',
      'Managed local network infrastructure, VoIP phones, and network-attached devices',
      'Provided in-person and remote end-user support',
      'Collaborated with third-party IT providers to support HIPAA-compliant systems',
      'Implemented basic security procedures and backup strategies'
    ]
  },
  {
    company: 'Earlier Career',
    role: 'Web & Design',
    location: '',
    period: 'Prior Experience',
    isCurrent: false,
    responsibilities: [
      'Began career in web development and digital design, building websites and supporting small business IT environments',
      'Established a strong technical foundation and comfort working with code, systems, and troubleshooting across platforms'
    ]
  }
];

export const ExperienceSection = () => {
  return (
    <section id="experience" className="py-20 md:py-28 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Work Experience
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full mb-6" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            A career journey focused on IT infrastructure, network operations, 
            and enterprise-level support.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-transparent" />

          {/* Experience Cards */}
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div key={index} className="relative pl-12 md:pl-20">
                {/* Timeline dot */}
                <div className={`absolute left-2.5 md:left-6.5 w-4 h-4 rounded-full border-4 ${
                  exp.isCurrent 
                    ? 'bg-primary border-primary-soft' 
                    : 'bg-background border-primary/40'
                }`} style={{ top: '28px' }} />
                
                <Card className={`border-border hover:shadow-lg transition-shadow ${
                  exp.isCurrent ? 'border-l-4 border-l-primary' : ''
                }`}>
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-bold text-xl text-foreground">
                            {exp.company}
                          </h3>
                          {exp.isCurrent && (
                            <Badge variant="cert" className="text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        <p className="text-primary font-medium">{exp.role}</p>
                      </div>
                      <div className="flex flex-col items-start sm:items-end gap-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={14} />
                          <span>{exp.period}</span>
                        </div>
                        {exp.location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin size={14} />
                            <span>{exp.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Responsibilities */}
                    <ul className="space-y-2">
                      {exp.responsibilities.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
